import { useState, useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { RiInformationLine } from "react-icons/ri";
import { FiCheckCircle } from "react-icons/fi";
import { FiXCircle } from "react-icons/fi";
import FileUploadCard from "../file-upload-card";
import { FileAttachment } from "~/stores/slices/connection-attachments";
import { useGlobalStore } from "~/stores/store-provider";

interface DatabaseConnectionForm {
  connectionName: string;
  user: string;
  password: string;
  account: string;
  database: string;
  role: string;
  warehouse: string;
  privateKey: string;
}

interface SnowflakeConnectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SnowflakeConnector = ({
  open,
  onOpenChange,
}: SnowflakeConnectorProps) => {
  const modalRef = useRef<HTMLDialogElement>(null);

  const [formData, setFormData] = useState<DatabaseConnectionForm>({
    connectionName: "",
    user: "",
    password: "",
    account: "",
    database: "",
    role: "",
    warehouse: "",
    privateKey: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCalloutExiting, setIsCalloutExiting] = useState<boolean>(false);
  const { addConnection, attachments, deleteAllAttachments, resetAttachments } =
    useGlobalStore((state) => state);

  // Handle dialog open/close with native methods
  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.showModal();
    } else if (!open && modalRef.current) {
      modalRef.current.close();
    }
  }, [open]);

  const closeModal = async () => {
    // Delete all attachments when modal closes to prevent orphaned files
    await deleteAllAttachments();
    modalRef.current?.close();
    onOpenChange(false);
  };

  // Reset form when dialog closes (component stays mounted)
  useEffect(() => {
    if (!open) {
      setFormData({
        connectionName: "",
        user: "",
        password: "",
        account: "",
        database: "",
        role: "",
        warehouse: "",
        privateKey: "",
      });
      resetAttachments();
      setIsLoading(false);
      setIsConnected(null);
      setIsCalloutExiting(false);
    }
  }, [open, resetAttachments]);

  const handleInputChange = (
    field: keyof DatabaseConnectionForm,
    value: string
  ) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const isFormValid = () => {
    // Check if we're using password authentication
    const isPasswordAuth =
      formData.user.trim() !== "" || formData.password.trim() !== "";

    if (isPasswordAuth) {
      return (
        formData.connectionName.trim() !== "" &&
        formData.user.trim() !== "" &&
        formData.password.trim() !== "" &&
        formData.account.trim() !== "" &&
        formData.database.trim() !== ""
      );
    } else {
      // Private key authentication
      return (
        formData.connectionName.trim() !== "" &&
        formData.account.trim() !== "" &&
        formData.database.trim() !== "" &&
        formData.privateKey.trim() !== ""
      );
    }
  };

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      const { privateKey, user, password, ...connectionData } = formData;
      const mappedAttachments = attachments.map(
        (attachment: FileAttachment) => ({
          description: attachment.description,
          file_id: attachment.fileId,
          file_name: attachment.fileName,
        })
      );

      privateKey.length > 0
        ? await addConnection({
            ...connectionData,
            privateKey,
            attachments: mappedAttachments,
          })
        : await addConnection({
            ...connectionData,
            user,
            password,
            attachments: mappedAttachments,
          });

      // Clear form data after successful connection
      setFormData({
        connectionName: "",
        user: "",
        password: "",
        account: "",
        database: "",
        role: "",
        warehouse: "",
        privateKey: "",
      });
      resetAttachments();

      setIsConnected(true);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 2700);

      setTimeout(() => {
        onOpenChange(false);
      }, 2000);
    } catch {
      setIsLoading(false);
      setIsConnected(false);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 4000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Connect to Snowflake</h3>
          <button
            className="btn btn-sm btn-circle btn-ghost"
            onClick={closeModal}
          >
            <IoClose />
          </button>
        </div>

        <div>
          <div className="tabs tabs-border">
            <input
              type="radio"
              name="snowflake_auth_tabs"
              className="tab"
              aria-label="Credentials"
              defaultChecked
              onChange={() => {
                setFormData((prev) => ({
                  ...prev,
                  connectionName: "",
                  user: "",
                  password: "",
                  account: "",
                  database: "",
                  role: "",
                  warehouse: "",
                  privateKey: "",
                }));
              }}
            />
            <div className="tab-content mt-4 p-1">
              <div className="flex flex-col gap-3">
                {/* Connection Name */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">
                        Connection Name
                      </span>
                      <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter a name for this connection"
                    value={formData.connectionName}
                    onChange={(e) =>
                      handleInputChange("connectionName", e.target.value)
                    }
                  />
                </div>

                {/* User */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">USER</span>
                      <span className="text-red-500">*</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake username"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your user"
                    value={formData.user}
                    onChange={(e) => handleInputChange("user", e.target.value)}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">PASSWORD</span>
                      <span className="text-red-500">*</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake password"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="password"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                  />
                </div>

                {/* Account */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">ACCOUNT</span>
                      <span className="text-red-500">*</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake account identifier"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your account"
                    value={formData.account}
                    onChange={(e) =>
                      handleInputChange("account", e.target.value)
                    }
                  />
                </div>

                {/* Database */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">DB</span>
                      <span className="text-red-500">*</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake database name"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your db"
                    value={formData.database}
                    onChange={(e) =>
                      handleInputChange("database", e.target.value)
                    }
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">ROLE</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake role"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your role"
                    value={formData.role}
                    onChange={(e) => handleInputChange("role", e.target.value)}
                  />
                </div>

                {/* Warehouse */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">WAREHOUSE</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake warehouse"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your warehouse"
                    value={formData.warehouse}
                    onChange={(e) =>
                      handleInputChange("warehouse", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            <input
              type="radio"
              name="snowflake_auth_tabs"
              className="tab"
              aria-label="Private Key"
              onChange={() => {
                setFormData((prev) => ({
                  ...prev,
                  connectionName: "",
                  user: "",
                  password: "",
                  account: "",
                  database: "",
                  role: "",
                  warehouse: "",
                  privateKey: "",
                }));
              }}
            />
            <div className="tab-content mt-4 p-1">
              <div className="flex flex-col gap-3">
                {/* Connection Name */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">
                        Connection Name
                      </span>
                      <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter a name for this connection"
                    value={formData.connectionName}
                    onChange={(e) =>
                      handleInputChange("connectionName", e.target.value)
                    }
                  />
                </div>

                {/* Private Key */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">
                        PRIVATE KEY
                      </span>
                      <span className="text-red-500">*</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your private key for authentication"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full mt-2"
                    placeholder="Enter your private key"
                    value={formData.privateKey}
                    onChange={(e) =>
                      handleInputChange("privateKey", e.target.value)
                    }
                    rows={5}
                  />
                </div>

                {/* Account */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">ACCOUNT</span>
                      <span className="text-red-500">*</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake account identifier"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your account"
                    value={formData.account}
                    onChange={(e) =>
                      handleInputChange("account", e.target.value)
                    }
                  />
                </div>

                {/* Database */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">DB</span>
                      <span className="text-red-500">*</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake database name"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your db"
                    value={formData.database}
                    onChange={(e) =>
                      handleInputChange("database", e.target.value)
                    }
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">ROLE</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake role"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your role"
                    value={formData.role}
                    onChange={(e) => handleInputChange("role", e.target.value)}
                  />
                </div>

                {/* Warehouse */}
                <div>
                  <label className="label">
                    <div className="flex items-center gap-1">
                      <span className="label-text font-medium">WAREHOUSE</span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Your Snowflake warehouse"
                      >
                        <RiInformationLine className="text-gray-500" />
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full mt-2"
                    placeholder="Enter your warehouse"
                    value={formData.warehouse}
                    onChange={(e) =>
                      handleInputChange("warehouse", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <FileUploadCard />

          <button
            className="mt-4 btn btn-neutral bg-cyan-600 text-white hover:bg-cyan-500 w-full"
            onClick={handleConnect}
            disabled={!isFormValid() || isLoading}
          >
            {isLoading ? (
              <span className="loading loading-spinner"></span>
            ) : (
              "Connect"
            )}
          </button>
        </div>
      </div>
      {isConnected !== null && (
        <div className="toast toast-top toast-center z-50">
          <div
            className={`alert ${
              isConnected ? "alert-success" : "alert-error"
            } ${
              isCalloutExiting
                ? "opacity-0 -translate-y-full"
                : "opacity-100 translate-y-0"
            } transition-all duration-300 ease-in-out`}
          >
            {isConnected ? (
              <FiCheckCircle className="h-6 w-6" />
            ) : (
              <FiXCircle className="h-6 w-6" />
            )}
            <span>
              {isConnected ? "Connection successful" : "Connection failed"}
            </span>
          </div>
        </div>
      )}
    </dialog>
  );
};

export default SnowflakeConnector;
